import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, X, Loader2,
  CheckCircle2, Clock, Save, AlertCircle, Sparkles,
  BookOpen,
} from 'lucide-react';
import toast from '../../utils/toast';
import DashboardLayout from '../Layout/DashboardLayout';
import ConfirmModal from '../UI/ConfirmModal';

/**
 * AssessmentRunner — the shared take-page experience for talent
 * assessments (Big Five, future inventories).
 *
 * Extracted from the original `SelfServiceBigFiveTake` page; that page is
 * now a thin wrapper that supplies a `config` object:
 *
 *   {
 *     brandLabel:     'Big Five Profile',
 *     prompt:         'How accurate is this statement about you?',
 *     submitLabel:    'Submit Big Five Profile',
 *     successToast:   'All done! Your Big Five Profile is ready.',
 *     homeRoute:      '/self-service/big-five',
 *     resultRoute:    '/self-service/big-five/result',
 *     api: {
 *       getBundle:    (language) => Promise,
 *       getSession:   (sessionId) => Promise,
 *       saveResponse: (sessionId, payload) => Promise,
 *       submitSession:(sessionId) => Promise,
 *     },
 *     findGlossaryMatches: (text) => [{displayKey, definition}], // optional
 *   }
 *
 * Design intent
 * -------------
 *   - Premium, full-width canvas at lg+ (max-w-7xl), single-column on mobile.
 *   - Sticky top progress bar + a sticky "session" rail on the left at lg+
 *     containing a circular progress ring, a persistent tone-setting tip
 *     panel, and keyboard hints.
 *   - Wide, generous question card on the right with large statement
 *     typography and refined scale buttons (gradient-fill on selected,
 *     hover-lift on hover).
 *
 * Psychometric constraints
 * ------------------------
 *   1. The dimension a question maps to is NEVER displayed (would bias
 *      responses). The server already strips that data from /items/.
 *
 *   2. We do NOT add interpretive helper text to individual items —
 *      inventories are validated against the verbatim wording, and
 *      paraphrasing or hinting would change what the item measures. What
 *      we DO render under questions, where a `findGlossaryMatches`
 *      function is supplied, is a small "Word meanings" line — a
 *      vocabulary glossary only, never a restatement or interpretation
 *      of the question itself.
 *
 *   3. The tone-setter on the left ("there are no right answers… answer
 *      based on how you generally are… first instinct is usually most
 *      accurate") is a generic instructional preamble of the kind that
 *      personality inventories conventionally include and does not bias
 *      responses to any particular item.
 *
 * Save & resume
 * -------------
 *   Each answer auto-saves server-side; the session's `current_position`
 *   is updated so the user can resume from this exact question on any
 *   device.
 */
const AssessmentRunner = ({ config }) => {
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const { api, brandLabel, prompt, submitLabel, successToast, homeRoute, resultRoute } = config;
  const findGlossaryMatches = config.findGlossaryMatches || null;

  const [bundle, setBundle] = useState(null);
  const [responses, setResponses] = useState({});       // { itemCode: { value, savedAt } }
  const [savingItem, setSavingItem] = useState(null);   // itemCode currently being saved
  const [errorItem, setErrorItem] = useState(null);     // itemCode that failed
  const [position, setPosition] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [direction, setDirection] = useState('forward'); // for slide animation
  const [loading, setLoading] = useState(true);
  const [confirmAction, setConfirmAction] = useState(null);
  const advanceTimerRef = useRef(null);
  const questionStartRef = useRef(Date.now());

  // -----------------------------------------------------------------------
  // Initial load: fetch bundle + session
  // -----------------------------------------------------------------------
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [bundleResp, sessionResp] = await Promise.all([
          api.getBundle('en'),
          api.getSession(sessionId),
        ]);
        if (!mounted) return;
        setBundle(bundleResp.data);
        const startPos = Math.max(1, Math.min(
          bundleResp.data?.items?.length || 1,
          sessionResp.data?.current_position || 1,
        ));
        setPosition(startPos);
      } catch (err) {
        toast.error('Could not load the assessment. Please try again.');
        navigate(homeRoute);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, navigate]);

  // Reset per-question timer whenever the position changes
  useEffect(() => {
    questionStartRef.current = Date.now();
    return () => {
      if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
    };
  }, [position]);

  // -----------------------------------------------------------------------
  // Save a single response
  // -----------------------------------------------------------------------
  const saveResponse = useCallback(async (itemCode, value, opts = {}) => {
    setSavingItem(itemCode);
    setErrorItem(null);
    const responseDurationMs = Date.now() - questionStartRef.current;
    try {
      const { data } = await api.saveResponse(sessionId, {
        item_code: itemCode,
        response_value: value,
        response_duration_ms: Math.max(0, responseDurationMs),
        current_position: opts.nextPosition || undefined,
      });
      setResponses((prev) => ({
        ...prev,
        [itemCode]: { value, savedAt: data.server_saved_at },
      }));
      return true;
    } catch (err) {
      setErrorItem(itemCode);
      toast.error('Could not save that answer. Please try again.');
      return false;
    } finally {
      setSavingItem(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // -----------------------------------------------------------------------
  // Answer + navigate
  // -----------------------------------------------------------------------
  const totalCount = bundle?.items?.length || 0;
  const currentItem = bundle?.items?.find((it) => it.position === position) || null;
  const currentValue = currentItem ? responses[currentItem.code]?.value : null;
  const answeredCount = Object.keys(responses).length;
  const isLastQuestion = position === totalCount;
  const allAnswered = answeredCount >= totalCount;

  const handleAnswer = async (value) => {
    if (!currentItem || submitting) return;
    if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);

    // Optimistic local update
    setResponses((prev) => ({
      ...prev,
      [currentItem.code]: { value, savedAt: null },
    }));

    const nextPos = isLastQuestion ? position : position + 1;
    const ok = await saveResponse(currentItem.code, value, { nextPosition: nextPos });

    if (ok && !isLastQuestion) {
      // Brief pause so user sees the tick, then auto-advance
      advanceTimerRef.current = setTimeout(() => {
        setDirection('forward');
        setPosition((p) => Math.min(totalCount, p + 1));
      }, 220);
    }
  };

  const goPrev = () => {
    if (position <= 1) return;
    setDirection('backward');
    setPosition((p) => Math.max(1, p - 1));
  };

  const goNext = () => {
    if (position >= totalCount) return;
    setDirection('forward');
    setPosition((p) => Math.min(totalCount, p + 1));
  };

  // -----------------------------------------------------------------------
  // Keyboard shortcuts
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (!currentItem) return undefined;
    const onKey = (e) => {
      if (e.target?.tagName === 'INPUT' || e.target?.tagName === 'TEXTAREA') return;
      if (e.key >= '1' && e.key <= '5') {
        e.preventDefault();
        handleAnswer(parseInt(e.key, 10));
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goNext();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentItem, position, totalCount]);

  // -----------------------------------------------------------------------
  // Submit
  // -----------------------------------------------------------------------
  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await api.submitSession(sessionId);
      toast.success(successToast);
      navigate(resultRoute);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'object' && detail?.responses) {
        toast.error(`Cannot submit yet — ${detail.responses}`);
      } else {
        toast.error('Could not submit your assessment. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleExit = () => {
    setExiting(true);
    navigate(homeRoute);
  };

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  if (loading || !bundle || !currentItem) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 text-blue-800 animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  const progressPct = Math.round((answeredCount / Math.max(1, totalCount)) * 100);
  const positionPct = Math.round((position / Math.max(1, totalCount)) * 100);
  const remaining = Math.max(0, totalCount - answeredCount);
  const scaleOptions = bundle.scale_options || [];

  // Vocabulary-only glossary for the current statement. Definitions
  // explain the WORD, never the question — see the file header.
  const glossaryMatches = findGlossaryMatches ? findGlossaryMatches(currentItem.text) : [];

  return (
    <DashboardLayout>
      {/* Edge-to-edge premium canvas — escape DashboardLayout's main padding
          so the gradient + orbs reach the full viewport on every screen. */}
      <div className="relative -m-4 md:-m-6 min-h-[calc(100dvh-4rem)] select-none overflow-hidden bg-gradient-to-br from-slate-50 via-white to-indigo-50/40">
        {/* Decorative blurred orbs — subtle premium ambience */}
        <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -top-32 -left-32 w-[28rem] h-[28rem] rounded-full bg-blue-200/30 blur-3xl" />
          <div className="absolute top-1/3 -right-40 w-[32rem] h-[32rem] rounded-full bg-violet-200/25 blur-3xl" />
          <div className="absolute -bottom-40 left-1/4 w-[28rem] h-[28rem] rounded-full bg-indigo-200/20 blur-3xl" />
        </div>

        {/* Sticky top bar — full viewport width */}
        <div className="sticky top-0 z-20 bg-white/75 backdrop-blur-md border-b border-slate-200/60 shadow-sm">
          <div className="px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-12 py-3 flex items-center gap-3 sm:gap-4">
            <button
              type="button"
              onClick={() => setConfirmAction('exit')}
              className="icon-button"
              aria-label="Exit"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between text-xs">
                <span className="font-semibold text-slate-700">
                  Question {position} <span className="text-slate-400 font-normal">of {totalCount}</span>
                </span>
                <span className="text-slate-500 tabular-nums">{progressPct}% answered</span>
              </div>
              <div className="mt-1.5 h-1.5 bg-slate-200/70 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-blue-700 via-blue-800 to-indigo-700 rounded-full shadow-sm transition-[width] duration-500 ease-out"
                  style={{ width: `${positionPct}%` }}
                />
              </div>
            </div>
            <SaveIndicator
              savingItem={savingItem}
              errorItem={errorItem}
              hasSavedAnswer={!!responses[currentItem.code]?.savedAt}
            />
          </div>
        </div>

        {/* Content grid — full viewport width */}
        <div className="relative px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-12 pt-6 sm:pt-10 lg:pt-12 pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 xl:gap-10">

            {/* LEFT RAIL — sticky session panel (lg+ only) */}
            <aside className="lg:col-span-4 xl:col-span-3 lg:sticky lg:top-24 lg:self-start space-y-4">
              {/* Hero — circular progress + brand */}
              <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 text-white p-6 shadow-2xl shadow-blue-900/25">
                <div aria-hidden className="absolute -top-20 -right-16 w-56 h-56 rounded-full bg-white/10 blur-2xl" />
                <div aria-hidden className="absolute -bottom-24 -left-16 w-56 h-56 rounded-full bg-violet-500/20 blur-3xl" />

                <div className="relative z-10">
                  <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-blue-200 font-semibold">
                    <Sparkles className="w-3.5 h-3.5" />
                    {brandLabel}
                  </div>

                  <div className="mt-5 flex items-center gap-5">
                    <ProgressRing answered={answeredCount} total={totalCount} />
                    <div className="min-w-0">
                      <div className="text-3xl font-bold leading-none tabular-nums">
                        {answeredCount}
                        <span className="text-blue-300 text-lg font-medium"> / {totalCount}</span>
                      </div>
                      <div className="mt-1.5 text-xs text-blue-200">
                        {remaining === 0 ? 'All answered' : `${remaining} to go`}
                      </div>
                    </div>
                  </div>

                  <p className="mt-5 text-xs leading-relaxed text-blue-100/90">
                    Answer honestly — there are no right or wrong responses, only what feels true for you.
                  </p>
                </div>
              </div>

              {/* Tone-setter — answering tips that apply across the whole
                  assessment. Visible on every question on lg+ as a calm
                  reminder; on smaller screens it appears as a compact
                  strip directly above the question card (see below). */}
              <div className="hidden lg:block rounded-2xl bg-white/70 backdrop-blur border border-slate-200/70 p-5 shadow-sm">
                <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-3">
                  How to answer
                </div>
                <ul className="space-y-2 text-xs text-slate-700 leading-relaxed">
                  <li className="flex items-start gap-2">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-blue-800 flex-shrink-0" />
                    <span>There are no right or wrong answers.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-blue-800 flex-shrink-0" />
                    <span>
                      Answer based on how you <em>generally</em> are — not how you'd
                      <em> like</em> to be, or how others might want you to be.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-blue-800 flex-shrink-0" />
                    <span>
                      Your first instinct is usually the most accurate. Don't overthink it.
                    </span>
                  </li>
                </ul>
              </div>

              {/* Keyboard hints — desktop only */}
              <div className="hidden lg:block rounded-2xl bg-white/70 backdrop-blur border border-slate-200/70 p-4 shadow-sm">
                <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-2.5">
                  Keyboard shortcuts
                </div>
                <div className="text-xs text-slate-600 flex flex-wrap items-center gap-x-2 gap-y-2.5">
                  <Kbd>1</Kbd>
                  <span className="text-slate-400">…</span>
                  <Kbd>5</Kbd>
                  <span className="text-slate-500">to answer</span>
                  <span className="text-slate-300 mx-1">·</span>
                  <Kbd>←</Kbd>
                  <Kbd>→</Kbd>
                  <span className="text-slate-500">to navigate</span>
                </div>
              </div>
            </aside>

            {/* RIGHT — question card */}
            <section className="lg:col-span-8 xl:col-span-9 min-w-0">
              {/* Mobile-only compact tone-setter — collapsed to a single
                  line so it doesn't crowd the question card on phones.
                  The full version lives in the left rail on lg+. */}
              <div className="lg:hidden mb-4 rounded-2xl bg-white/80 backdrop-blur border border-slate-200/70 px-4 py-3 text-xs text-slate-700 leading-relaxed shadow-sm">
                <span className="font-semibold text-slate-800">No right answers</span>
                <span className="text-slate-400"> · </span>
                Answer how you <em>generally</em> are
                <span className="text-slate-400"> · </span>
                First instinct is best
              </div>

              <div
                key={currentItem.code}
                className={`relative rounded-3xl bg-white ring-1 ring-slate-200/70 shadow-2xl shadow-slate-900/5 p-6 sm:p-10 lg:p-12 xl:p-14 min-h-[300px] sm:min-h-[360px] lg:min-h-[420px] flex flex-col ${
                  direction === 'forward' ? 'animate-slide-in-right' : 'animate-slide-in-left'
                }`}
              >
                {/* Tiny instruction */}
                <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
                  {prompt}
                </div>

                {/* Statement — capped width keeps line length readable on ultra-wide monitors */}
                <p
                  className="mt-5 max-w-4xl text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-medium tracking-tight text-slate-900 leading-[1.15]"
                  style={{ textWrap: 'balance' }}
                >
                  {currentItem.text}
                </p>

                {/* Vocabulary glossary — shown ONLY when the current
                    statement contains a word from the inventory's
                    glossary. We render dictionary definitions only
                    (never an interpretation of the item), so the
                    validity of the inventory wording is preserved.
                    Designed to be visually quiet so it doesn't compete
                    with the statement itself. */}
                {glossaryMatches.length > 0 && (
                  <div className="mt-6 max-w-3xl rounded-2xl border border-slate-200/80 bg-slate-50/70 px-4 py-3.5 sm:px-5 sm:py-4">
                    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-2">
                      <BookOpen className="w-3.5 h-3.5" />
                      Word meanings
                    </div>
                    <ul className="space-y-1.5 text-xs sm:text-sm text-slate-700 leading-relaxed">
                      {glossaryMatches.map((m) => (
                        <li key={m.displayKey} className="flex flex-wrap gap-x-1.5">
                          <span className="font-semibold text-slate-900">
                            &ldquo;{m.displayKey.charAt(0).toUpperCase() + m.displayKey.slice(1)}&rdquo;
                          </span>
                          <span className="text-slate-400">—</span>
                          <span className="text-slate-700">{m.definition}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Scale buttons.
                    Layout notes:
                      - 5 equal columns on every breakpoint, but the cells
                        themselves can grow taller to accommodate
                        wrapped labels — `min-h-[…]` reserves enough
                        vertical room for the longest label to wrap to
                        two lines without pushing the layout around.
                      - The label `<span>` uses `break-words` +
                        `hyphens-auto` so even narrow phones keep the
                        text inside the button instead of overflowing.
                      - `overflow-hidden` clips any residual overflow
                        defensively (the rounded corners stay clean too). */}
                <div className="mt-auto pt-10 lg:pt-14 grid grid-cols-5 gap-1.5 sm:gap-3 lg:gap-4">
                  {scaleOptions.map((opt) => {
                    const isSelected = currentValue === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => handleAnswer(opt.value)}
                        disabled={submitting}
                        className={[
                          'group relative flex flex-col items-center justify-center gap-2 sm:gap-2.5 lg:gap-3 rounded-2xl px-1.5 sm:px-2 py-4 sm:py-6 lg:py-7',
                          'min-h-[120px] sm:min-h-[140px] lg:min-h-[160px]',
                          'overflow-hidden',
                          'border transition-all duration-200',
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2',
                          'disabled:opacity-50 disabled:cursor-not-allowed',
                          isSelected
                            ? 'border-transparent bg-gradient-to-br from-blue-700 to-blue-900 text-white shadow-xl shadow-blue-900/25 scale-[1.04]'
                            : 'border-slate-200 bg-white text-slate-800 hover:border-blue-400 hover:bg-blue-50/60 hover:-translate-y-0.5 hover:shadow-md',
                        ].join(' ')}
                        aria-pressed={isSelected}
                        title={opt.label}
                      >
                        <span className={[
                          'w-9 h-9 sm:w-10 sm:h-10 lg:w-11 lg:h-11 rounded-xl flex items-center justify-center text-sm sm:text-base lg:text-lg font-bold border transition-colors flex-shrink-0',
                          isSelected
                            ? 'bg-white/15 text-white border-white/25 backdrop-blur'
                            : 'bg-slate-50 text-slate-700 border-transparent group-hover:bg-blue-100 group-hover:text-blue-800',
                        ].join(' ')}>
                          {isSelected ? <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6" /> : opt.value}
                        </span>
                        {/* Two label variants:
                            - The compact `short_label` is shown on
                              the smallest screens where 5 columns
                              leave very little horizontal room.
                            - The full `label` is shown from `sm`
                              upwards. This preserves the validated
                              scale wording on every device that has
                              the room for it, without ever spilling
                              outside the button. */}
                        <span
                          className="sm:hidden min-w-0 max-w-full text-[10px] leading-tight text-center font-medium break-words hyphens-auto"
                          style={{ wordBreak: 'break-word' }}
                        >
                          {opt.short_label || opt.label}
                        </span>
                        <span
                          className="hidden sm:block min-w-0 max-w-full text-xs lg:text-sm leading-tight text-center font-medium break-words hyphens-auto"
                          style={{ wordBreak: 'break-word' }}
                        >
                          {opt.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Navigation */}
              <div className="mt-6 lg:mt-8 flex items-center justify-between">
                <button
                  type="button"
                  onClick={goPrev}
                  disabled={position <= 1}
                  className="btn btn-outline"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Back
                </button>

                {isLastQuestion ? (
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={!allAnswered || submitting}
                    className="btn btn-primary"
                  >
                    {submitting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        {submitLabel}
                        <ChevronRight className="w-4 h-4" />
                      </>
                    )}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={goNext}
                    disabled={position >= totalCount}
                    className="btn btn-outline"
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Final-page reminder */}
              {isLastQuestion && !allAnswered && (
                <div className="mt-6 rounded-2xl bg-amber-50/90 backdrop-blur border border-amber-200 p-4 text-sm text-amber-900 flex items-start gap-2 shadow-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>
                    You have {remaining} unanswered question{remaining === 1 ? '' : 's'}. Use Back or the navigation arrows to fill them in.
                  </span>
                </div>
              )}
            </section>
          </div>
        </div>
      </div>

      {/* Exit confirmation */}
      {confirmAction === 'exit' && (
        <ConfirmModal
          isOpen
          onCancel={() => setConfirmAction(null)}
          onConfirm={handleExit}
          isLoading={exiting}
          title="Save and exit?"
          message="Your progress is saved automatically. You can resume from this exact question on any device."
          confirmLabel="Save and exit"
          variant="primary"
          theme="employee"
        />
      )}

      <SlideAnimationCss />
    </DashboardLayout>
  );
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const SaveIndicator = ({ savingItem, errorItem, hasSavedAnswer }) => {
  if (errorItem) {
    return (
      <div className="text-xs text-red-700 inline-flex items-center gap-1.5 font-medium">
        <AlertCircle className="w-4 h-4" />
        <span className="hidden sm:inline">Save failed</span>
      </div>
    );
  }
  if (savingItem) {
    return (
      <div className="text-xs text-slate-500 inline-flex items-center gap-1.5 font-medium">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span className="hidden sm:inline">Saving</span>
      </div>
    );
  }
  if (hasSavedAnswer) {
    return (
      <div className="text-xs text-emerald-700 inline-flex items-center gap-1.5 font-medium">
        <Save className="w-4 h-4" />
        <span className="hidden sm:inline">Saved</span>
      </div>
    );
  }
  return (
    <div className="text-xs text-slate-400 inline-flex items-center gap-1.5 font-medium">
      <Clock className="w-4 h-4" />
      <span className="hidden sm:inline">Auto-save on</span>
    </div>
  );
};

/**
 * Circular progress ring (SVG). Used in the session hero to show
 * answered-vs-total at a glance.
 */
const ProgressRing = ({ answered, total, size = 76, stroke = 6 }) => {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = total > 0 ? Math.min(1, Math.max(0, answered / total)) : 0;
  const offset = c * (1 - pct);
  return (
    <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="rgba(255,255,255,0.15)"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="white"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 600ms cubic-bezier(0.4, 0, 0.2, 1)' }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-sm font-bold text-white tabular-nums">
          {Math.round(pct * 100)}%
        </span>
      </div>
    </div>
  );
};

const Kbd = ({ children }) => (
  <kbd className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1.5 rounded-md border border-slate-300 bg-white text-[11px] font-semibold text-slate-700 shadow-sm font-sans">
    {children}
  </kbd>
);

// Tiny inline keyframes so we don't need to touch the global stylesheet.
const SlideAnimationCss = () => (
  <style>{`
    @keyframes ta-slide-in-right { from { opacity: 0; transform: translateX(20px); } to { opacity: 1; transform: translateX(0); } }
    @keyframes ta-slide-in-left  { from { opacity: 0; transform: translateX(-20px); } to { opacity: 1; transform: translateX(0); } }
    .animate-slide-in-right { animation: ta-slide-in-right 220ms cubic-bezier(0.4, 0, 0.2, 1); }
    .animate-slide-in-left  { animation: ta-slide-in-left  220ms cubic-bezier(0.4, 0, 0.2, 1); }
  `}</style>
);

export default AssessmentRunner;
